import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Permission constants
 * These should match the backend permission strings
 */
export const PERMISSIONS = {
  LAUNCH_CAMPAIGN: 'LAUNCH_CAMPAIGN',
  VIEW_POST_ANALYSIS: 'VIEW_POST_ANALYSIS',
  VIEW_ADVANCED_POST_ANALYSIS: 'VIEW_ADVANCED_POST_ANALYSIS',
  VIEW_CAMPAIGN_MANAGEMENT_DASHBOARD: 'VIEW_CAMPAIGN_MANAGEMENT_DASHBOARD',
  MANAGE_USERS: 'MANAGE_USERS',
  VIEW_BUSINESS_DASHBOARD: 'VIEW_BUSINESS_DASHBOARD',
  VIEW_ADMIN_DASHBOARD: 'VIEW_ADMIN_DASHBOARD',
};

/**
 * Role constants
 */
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  BUSINESS_ADMIN: 'BUSINESS_ADMIN',
  USER: 'USER',
};

/**
 * Custom hook to manage user permissions
 * @returns {object} Permission utilities
 */
export function usePermissions() {
  const { user, isAuthenticated } = useAuth();

  // Extract permissions and role from user object
  const permissions = useMemo(() => {
    if (!user) return [];
    return user.permissions || [];
  }, [user]);

  const role = useMemo(() => {
    if (!user) return null;
    return user.role || user.user_role || null;
  }, [user]);

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission string to check
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!isAuthenticated || !permissions) return false;
    return permissions.includes(permission);
  };

  /**
   * Check if user has any of the given permissions
   * @param {string[]} permissionList - Array of permission strings
   * @returns {boolean}
   */
  const hasAnyPermission = (permissionList) => {
    if (!isAuthenticated || !permissions) return false;
    return permissionList.some((permission) => permissions.includes(permission));
  };

  /**
   * Check if user has all of the given permissions
   * @param {string[]} permissionList - Array of permission strings
   * @returns {boolean}
   */
  const hasAllPermissions = (permissionList) => {
    if (!isAuthenticated || !permissions) return false;
    return permissionList.every((permission) => permissions.includes(permission));
  };

  /**
   * Check if user has a specific role
   * @param {string} roleToCheck - Role string to check
   * @returns {boolean}
   */
  const hasRole = (roleToCheck) => {
    if (!isAuthenticated || !role) return false;
    return role === roleToCheck;
  };

  /**
   * Check if user has any of the given roles
   * @param {string[]} roleList - Array of role strings
   * @returns {boolean}
   */
  const hasAnyRole = (roleList) => {
    if (!isAuthenticated || !role) return false;
    return roleList.includes(role);
  };

  /**
   * Check if user is a Super Admin
   * @returns {boolean}
   */
  const isSuperAdmin = () => {
    return hasRole(ROLES.SUPER_ADMIN);
  };

  /**
   * Check if user is a Business Admin
   * @returns {boolean}
   */
  const isBusinessAdmin = () => {
    return hasRole(ROLES.BUSINESS_ADMIN);
  };

  return {
    permissions,
    role,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isBusinessAdmin,
  };
}

export default usePermissions;
